/**
 * «+» карточки игры на доске расстановки ОО — выбор оператора (16.10: вынесено из OoAssignScreen.tsx и дополнено).
 * Канон (oo-board-canon): компактный, у плюсика, без подложки, одна колонка по алфавиту, вверх при нехватке места
 * снизу, тап мимо — закрыть. Квадратик (26.08, владелец) — «может ли провести игру на всём протяжении по графику».
 * 16.10 (владелец): рядом — грейд оператора на этом квесте и сколько игр на нём он отвёл за всё время; уже стоит на
 * пересекающейся игре — квадратик в красной рамке; долгое нажатие по строке (450 мс, как меню чипа) — под строкой
 * с чем пересечение и что по графику. Тап — поставить, как раньше; хвостовой клик после долгого нажатия не ставит.
 * 16.10 (владелец): шапка «операторы · грейд · игр · график» — тап по столбцу сортирует (первый — по убыванию, второй —
 * по возрастанию; «операторы» — по алфавиту, по умолчанию). Выбор помнится до перезагрузки: следующий «+» — так же.
 * 16.10 (владелец): долгое нажатие показывает, куда оператора УЖЕ поставили в этот день — все его игры на доске
 * (пересекающиеся — красным), МК, учебная игра, админ зала, допработы — по черновикам доски, как они видны сейчас.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import type { BoardGame, BoardOperator } from "../../hooks/useOoDay";
import { tgHaptic } from "../../lib/telegram";
import { canWholeSpan } from "./canWhole";
import { rankOperators, type AssignMap, type HallAdminMap } from "./scoring";
import { DEFAULT_SORT, clashText, dayOf, dayText, gamesWord, gradeKey, gradeLabel, nextSort, sortRows, useOpStats, type OpSort, type SortKey } from "./opStats";
import { otherActs, useDayBusy } from "./dayBusy";
import css from "./board.module.css";

const HOLD_MS = 450;
const COLS: [SortKey, string, string][] = [["name", "операторы", "по имени"], ["grade", "грейд", "по грейду"], ["games", "игр", "по числу игр"], ["sched", "график", "по графику"]];
let lastSort: OpSort = DEFAULT_SORT;   // до перезагрузки: ОО отсортировал по грейду — следующий «+» откроется так же

interface Props {
  game: BoardGame;
  crew: string[];
  allGames: BoardGame[];
  draft: AssignMap;
  operators: BoardOperator[];
  hallAdmins: HallAdminMap;
  onPick: (opId: string) => void;
  onClose: () => void;
}

export function OpDropdown({ game, crew, allGames, draft, operators, hallAdmins, onPick, onClose }: Props) {
  const crewGenders = crew.map((id) => operators.find((o) => o.id === id)?.gender ?? "").filter(Boolean);
  useEffect(() => { // компактный дропдаун у плюсика (20.08, Саша): без подложки, тап мимо — закрыть
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.rhAddWrap}`)) onClose(); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [onClose]);
  /* всегда в пределах экрана (21.08, Саша): мало места снизу → открываемся вверх, высота — по свободному месту */
  const ddRef = useRef<HTMLDivElement | null>(null); const [pos, setPos] = useState<{ up: boolean; maxH: number } | null>(null);
  useLayoutEffect(() => {
    const r = ddRef.current?.parentElement?.getBoundingClientRect(); if (!r) return;
    const below = window.innerHeight - r.bottom - 10, above = r.top - 10, up = below < 170 && above > below;
    setPos({ up, maxH: Math.max(120, Math.min(window.innerHeight * 0.46, up ? above : below)) });
  }, []);
  const stats = useOpStats(game.questId);
  const dayBusy = useDayBusy();
  const [info, setInfo] = useState<string | null>(null);
  const [sort, setSort] = useState<OpSort>(lastSort);
  const pickSort = (k: SortKey) => setSort((s) => (lastSort = nextSort(s, k)));
  const noteRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (info) noteRef.current?.scrollIntoView({ block: "nearest" }); }, [info]);   // у нижней строки — не за краем
  const hold = useRef<{ id: string; x: number; y: number; t: number } | null>(null);
  const fired = useRef<string | null>(null);
  useEffect(() => () => { if (hold.current) window.clearTimeout(hold.current.t); }, []);
  const down = (id: string, e: RPointerEvent) => {
    fired.current = null;   // новый жест: хвост прошлого долгого нажатия не глотает этот тап
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (hold.current) window.clearTimeout(hold.current.t);
    const t = window.setTimeout(() => { fired.current = id; hold.current = null; tgHaptic("tap"); setInfo((x) => (x === id ? null : id)); }, HOLD_MS);
    hold.current = { id, x: e.clientX, y: e.clientY, t };
  };
  const move = (e: RPointerEvent) => {
    const h = hold.current;
    if (h && Math.hypot(e.clientX - h.x, e.clientY - h.y) > 10) { window.clearTimeout(h.t); hold.current = null; }   // прокрутка списка
  };
  const up = () => { if (hold.current) { window.clearTimeout(hold.current.t); hold.current = null; } };

  /* 26.08 (владелец): один индикатор — зелёный/красный квадратик «может ли провести
     игру на всём протяжении по графику»; прежние ✓/⚠/×/«Nи» убраны. */
  const rows = rankOperators(operators, game, draft, allGames, hallAdmins, crewGenders)
    .filter((r) => !crew.includes(r.operatorId))
    .map((r) => {
      const op = operators.find((o) => o.id === r.operatorId), id = r.operatorId;
      const day = dayOf(id, game, draft, allGames, stats.spans), clashes = day.filter((c) => c.minutes > 0);
      const grade = stats.grades?.get(gradeKey(id, game.questId)) ?? 0, n = stats.played ? stats.played.get(id) ?? 0 : null;
      return { r, op, id, day, clashes, grade, n, name: op?.name ?? r.operatorName, games: n ?? 0, clash: clashes.length > 0,
        can: op ? canWholeSpan(op, game.time, game.duration || 60) : false };
    });
  /* ревью 16.10: порядок не прыгает под пальцем. Пока нет данных выбранного столбца — строк нет («сортирую…»), а не
     алфавит, который потом перестроится; посчитанный порядок держится, пока не сменят столбец или состав списка —
     поздно приехавшие цифры обновляют ячейки на месте. Сбой загрузки — по имени, как раньше. */
  const ready = sort.key === "games" ? stats.played != null || stats.failed : sort.key === "grade" ? stats.grades != null || stats.failed : true;
  const idsSig = rows.map((x) => x.id).sort().join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const order = useMemo(() => (ready ? sortRows(rows, sort).map((x) => x.id) : null), [sort, idsSig, ready]);
  const byId = new Map(rows.map((x) => [x.id, x]));
  const ranked = (order ?? []).flatMap((id) => byId.get(id) ?? []);

  return (
    <div ref={ddRef} className={`${css.opDrop} ${pos?.up ? css.opDropUp : ""}`} style={pos ? { maxHeight: pos.maxH } : { visibility: "hidden" }} role="menu" aria-label="Добавить оператора">
      {rows.length === 0 ? <div className={css.opEmpty}>Свободных нет</div> : (
        <div className={css.opHead}>
          {COLS.map(([k, label, how]) => (
            <button key={k} type="button" className={`${css.opHd} ${sort.key === k ? css.opHdOn : ""}`} aria-pressed={sort.key === k}
              aria-label={`Сортировать ${how}${sort.key === k ? (sort.dir === "desc" ? ", сейчас по убыванию" : ", сейчас по возрастанию") : ""}`} onClick={() => pickSort(k)}>
              {label}{sort.key === k ? <i className={css.opArr} aria-hidden="true">{sort.dir === "desc" ? "▼" : "▲"}</i> : null}
            </button>
          ))}
        </div>
      )}
      {rows.length && !order ? <div className={css.opEmpty}>Сортирую…</div> : null}
      {ranked.map(({ r, op, id, can, day, clashes, grade, n }) => {
        const others = info === id ? otherActs(id, allGames, dayBusy) : [];
        const title = [can ? "по графику свободен всю игру" : "по графику НЕ может всю игру", ...clashes.map((c) => `уже стоит: ${clashText(c)}`)].join("; ");
        return (
          <div key={id} className={css.opRowWrap}>
            <button type="button" className={`${css.opItem} ${css.opRow}`} title={title}
              onPointerDown={(e) => down(id, e)} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
              onContextMenu={(e) => { e.preventDefault(); if (fired.current === id) return; up(); fired.current = id; setInfo((x) => (x === id ? null : id)); }}
              onClick={() => { if (fired.current === id) { fired.current = null; return; } onPick(id); }}>
              <span className={css.opName}>{op?.name ?? r.operatorName}</span>
              <span className={`${css.opGr} ${grade ? "" : css.opDim}`}>{grade ? gradeLabel(grade) : stats.grades ? "—" : stats.failed ? "?" : "…"}</span>
              <span className={`${css.opN} ${n ? "" : css.opDim}`}>{n ?? (stats.failed ? "?" : "…")}</span>
              <span className={`${css.opSq} ${can ? css.opSqOk : css.opSqNo} ${clashes.length ? css.opSqClash : ""}`} aria-label={title} />
            </button>
            {info === id ? (
              <div ref={noteRef} className={css.opInfo} role="note">
                {day.length || others.length ? <div className={css.opInfoHd}>Уже стоит в этот день:</div> : <div>В этот день ещё никуда не поставлен</div>}
                {day.map((c) => <div key={c.id} className={c.minutes > 0 ? css.opInfoBad : ""}>{dayText(c)}</div>)}
                {others.map((t, i) => <div key={i}>{t}</div>)}
                <div>{can ? "По графику свободен всю игру" : "По графику не может всю игру"}</div>
                {n != null ? <div>{grade ? `Грейд ${gradeLabel(grade)}` : "Грейд не присвоен"} · {n} {gamesWord(n)} на {game.shortName || game.questName}</div> : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
